import { useState, useEffect, useCallback, useRef } from 'react'

export type LoadingPhase = 'idle' | 'shimmer' | 'title' | 'subtitle' | 'complete'

interface UseSequencedLoadingOptions {
  isRunning: boolean
  titleDelay?: number
  subtitleDelay?: number
}

interface UseSequencedLoadingReturn {
  phase: LoadingPhase
  showTitleShimmer: boolean
  showSubtitleShimmer: boolean
  titleActive: boolean
  subtitleActive: boolean
  onTitleComplete: () => void
  onSubtitleComplete: () => void
}

export function useSequencedLoading({
  isRunning,
  titleDelay = 2000,
  subtitleDelay = 1000,
}: UseSequencedLoadingOptions): UseSequencedLoadingReturn {
  const [phase, setPhase] = useState<LoadingPhase>('idle')
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const subtitleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (titleTimerRef.current) {
      clearTimeout(titleTimerRef.current)
      titleTimerRef.current = null
    }
    if (subtitleTimerRef.current) {
      clearTimeout(subtitleTimerRef.current)
      subtitleTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (isRunning) {
      setPhase('shimmer')
      clearTimers()

      titleTimerRef.current = setTimeout(() => {
        setPhase('title')
      }, titleDelay)
    } else {
      clearTimers()
      setPhase('idle')
    }

    return clearTimers
  }, [isRunning, titleDelay, clearTimers])

  const onTitleComplete = useCallback(() => {
    subtitleTimerRef.current = setTimeout(() => {
      setPhase('subtitle')
    }, subtitleDelay)
  }, [subtitleDelay])

  const onSubtitleComplete = useCallback(() => {
    setPhase('complete')
  }, [])

  const showTitleShimmer = phase === 'shimmer'
  const showSubtitleShimmer = phase === 'shimmer' || phase === 'title'
  const titleActive = phase === 'title' || phase === 'subtitle' || phase === 'complete'
  const subtitleActive = phase === 'subtitle' || phase === 'complete'

  return {
    phase,
    showTitleShimmer,
    showSubtitleShimmer,
    titleActive,
    subtitleActive,
    onTitleComplete,
    onSubtitleComplete,
  }
}
